import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import CommentSection from "@/components/article/CommentSection";
import ShareButton from "@/components/article/ShareButton";
import FeedbackSection from "@/components/article/FeedbackSection";
import ArticleStats from "@/components/article/ArticleStats";

const ArticleFaangInterviewsPart2 = () => {
  const articleId = 'faang-ml-interviews-part2';
  const articleTitle = "Level Up Your FAANG ML Interview – Tips for IC2 to IC6";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-8 md:py-12">
        <div className="container max-w-3xl">
          <div className="mb-8">
            <Link to="/learn">
              <Button variant="ghost" className="pl-0 flex items-center gap-2">
                <ArrowLeft size={16} />
                Back to resources
              </Button>
            </Link>
          </div>
          
          {/* Add Feedback Section before Comments */}
          <article className="prose prose-lg max-w-none">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{articleTitle}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <span>8 min read</span>
              <span>ML Fundamentals</span>
              <ArticleStats articleId={articleId} />
            </div>
            
            <div className="bg-muted/50 p-4 rounded-lg mb-6">
              <p className="italic text-muted-foreground">
                By Bharathi Priyaa · Published June 13, 2023
              </p>
            </div>
            
            <p className="lead">
              <Link to="/articles/faang-ml-interviews" className="text-primary hover:underline">Part 1</Link> was published last December and has proved to be useful to many readers, with lots of candidates reaching out for more pointers on approaching ML interviews. Hence this follow-up article.
            </p>
            
            <p>
              Machine learning system design rounds are increasingly becoming a standard assessment at top FAANG companies for ML engineers. The caveat is that ML System design interviews are still not formalized or standardized across the industry. This is both good and bad. It's good because, as a well-prepared candidate, you can really shine in this round compared to others. It's bad because, depending on the company and the interviewer, this round can be hit or miss.
            </p>

            <p>
              I have interviewed at 10+ Tier1 and Tier2 companies and secured offers for Staff ML levels from Google, Uber, Doordash, Nextdoor, Square, Glean, Roblox and others. I've also interviewed at smaller companies ahead of my Tier 1 onsite interviews. I'm currently a Principal Machine Learning Engineer at Roblox. I'll share tips that helped me secure onsite offers based on my experience with 7+ ML design interviews and 10+ mock interviews for colleagues and candidates.
            </p>

            <div className="bg-muted/50 p-4 rounded-lg mb-6">
              <p>
                If you haven't read my <Link to="/articles/faang-ml-interviews" className="text-primary hover:underline">previous article</Link>, please take a look. 
                I've also shared study resources there.
              </p>
              <p className="mb-0">
                This article focuses less on specific interview questions or prep materials, and more on criteria to watch for when interviewing at different levels. ML System design interviews are crucial for calibrating your actual IC level.
              </p>
            </div>

            <h2>The State of ML System Design Interviews</h2>
            
            <p>
              Most ML System design interview books don't give a complete picture of how candidates should approach this round. The industry standards are still evolving, and from my perspective, books on ML System design interviews (Alex Xu, Khang) don't precisely capture interviewer expectations. If you're on the job market, don't rely solely on these books.
            </p>
            
            <p>
              My main concerns about existing books:
            </p>
            
            <ul>
              <li>They try to cater to both ML Modeling engineers and ML Infra engineers (with Khang Pham's book leaning more toward Infra engineers)</li>
              <li>The structure caters more to L4 engineers than Senior/Staff/Senior staff engineers</li>
              <li>Content only touches the surface of topics like Feature engineering, Online metrics, offline metrics without the depth typically required in interviews</li>
            </ul>
            
            <p>
              Should you ignore these books? Certainly not. I read both books multiple times to prepare for interviews. Take useful parts from the books, but don't treat them as sufficient for your ML prep.
            </p>
            
            <h2>Level-Based Approach to ML System Design</h2>
            
            <p>
              Let's say you're asked to "Design a YouTube recommendation system." There's a typical structure to follow, as mentioned in my previous article. This template is common for all levels, but the depth of answers differentiates an L5 from an L6 candidate.
            </p>
            
            <h3>How an L4 Engineer Might Answer</h3>
            
            <p>
              An L4 engineer would typically be expected to complete project-level tasks in one component (i.e., Build a feature set for X, Y, Z, Train an offline model for existing dataset). We expect L4 engineers to know the basic building blocks.
            </p>
            
            <p>
              For a typical ML Design question with 5 parts, an L4 could spend:
            </p>
            
            <ul>
              <li><strong>First 10 mins:</strong> Giving an overview of the system</li>
              <li><strong>10 mins on feature engineering:</strong> How to encode categorical features, log features, embedding features</li>
              <li><strong>10 mins on modeling:</strong> How to model the problem using logistic regression vs. decision trees, covering topics like cross entropy, pros/cons of LR and GBDT, differences between GBDT and XGBoost, and parameter tuning</li>
            </ul>
            
            <p>
              <strong>Common L4 Red Flags:</strong>
            </p>
            
            <ul>
              <li>Not presenting an overview of the problem or inability to decompose the ML design into a fundamental classification approach</li>
              <li>Switching haphazardly between sections (e.g., starting with an objective function, moving to features, then returning to change the objective function)</li>
            </ul>
            
            <h3>How an L5 Engineer Might Answer</h3>
            
            <p>
              An L5 Engineer is typically expected to go deep in one component and cross-collaborate with two or more components outside their scope (e.g., Build the ML library for two-tower model and deploy the first version, Build the A/B experiment interleaving platform, Build the contextual ranking modeling system).
            </p>
            
            <p>
              L5 Engineers should:
            </p>
            
            <ul>
              <li>Show the depth that an L4 engineer shows — typically within the first 15 mins — and then move to more novel concepts</li>
              <li>Give an overview of the entire system — from objective function to training data generation, model building, and deployment</li>
              <li>Provide trade-offs for each option:
                <ul>
                  <li><strong>Objective function:</strong> Compare pairwise learning vs. pointwise learning</li>
                  <li><strong>Training data generation:</strong> Give 2-3 options (e.g., sessionized training data, weighting negative samples, addressing popularity bias, handling new users)</li>
                  <li><strong>Model building:</strong> Contrast different options without spending too much time here</li>
                  <li><strong>Deployment:</strong> Cover A/B experimentation (interleaving vs. non-interleaving), user exposure, and minimum detectable effect</li>
                </ul>
              </li>
              <li>Address offline-online metric skew without being explicitly asked</li>
              <li>Spend minimal time on feature engineering (this is where L4s shine, but not a focus for L5s)</li>
            </ul>
            
            <p>
              <strong>Common L5 Red Flags:</strong>
            </p>
            
            <ul>
              <li>Spending too much time on model architecture or feature engineering</li>
              <li>Not asking clarifying questions about corpus size, QPS, and real-time prediction needs</li>
              <li>Proposing a deep neural network when your corpus size is &lt;5000</li>
              <li>Immediately recommending complex models like Deep Cross Networks without considering simpler algorithms</li>
              <li>Neglecting to discuss productionization, the real bottleneck for production-grade ML systems</li>
              <li>Applying the same multi-stage system to every problem, even when simpler solutions would suffice</li>
            </ul>
            
            <h3>How an L6 & Above Engineer Might Answer</h3>
            
            <p>
              L6 engineers typically lead entire domains and have cross-organizational impact. At larger companies like Meta, 3-4 L6 folks might lead CTR ads prediction for a particular surface, while at smaller startups they would build entire systems from scratch.
            </p>
            
            <p>
              Examples of L6-level problems:
            </p>
            
            <ul>
              <li>"Design the first ads prediction system for a meal delivery startup"</li>
              <li>"Design and implement the first ranking model for homepage personalization"</li>
            </ul>
            
            <p>
              To demonstrate the required depth, L6 engineers should:
            </p>
            
            <ul>
              <li>Cover what an L5 would in the first 15 mins, then move to more advanced topics</li>
              <li>Provide trade-offs for each section (a must)</li>
              <li>Skip easier sections like feature engineering and model intricacies</li>
              <li>Address nuanced concepts in detail:
                <ul>
                  <li>Popularity bias</li>
                  <li>Introducing diversity in ranking</li>
                  <li>Repetition penalties</li>
                  <li>Label leakage/novelty effects</li>
                </ul>
              </li>
              <li>Call out best practices:
                <ul>
                  <li>Resource allocation decisions (e.g., with 5 engineers, would you build a simple logistic regression model or a multi-stage recommendation system?)</li>
                  <li>Downsides of multi-stage recommendations</li>
                  <li>Team practices for building scalable, high-ROI teams</li>
                  <li>2+ years' worth of vision</li>
                  <li>Privacy-focused modeling approaches</li>
                  <li>Developer efficiency improvements (platformization of repeated processes)</li>
                </ul>
              </li>
              <li>Think about building new team scope while taking a long-term view and reducing overlaps with other teams</li>
            </ul>
            
            <h2>Final Thoughts</h2>
            
            <p>
              I hope this article helps you understand the different expectations at each level during ML system design interviews. I plan to delve deeper into commonly asked ML design questions in future articles. Perhaps one day I'll write a book!
            </p>
            
            <p>
              For now, I offer ML design mock interviews and provide a <a href="https://calendly.com/reachpriyaa/mock-ml-interview" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">booking link</a> if you'd like to practice with me.
            </p>
            
            <p>
              Please let me know in the comments if you'd like more articles like this!
            </p>
            
            <div className="mt-8 border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <Link to="/articles/faang-ml-interviews">
                  <Button variant="outline" className="flex items-center gap-2">
                    <ArrowLeft size={16} />
                    Read Part 1
                  </Button>
                </Link>
                <ShareButton title="Part 2: Cracking ML Interviews at FAANG" />
              </div>
            </div>
            
            {/* Add Feedback Section */}
            <FeedbackSection articleTitle={articleTitle} articleId={articleId} />
          </article>
          
          <CommentSection articleId={articleId} />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ArticleFaangInterviewsPart2;
